import { useContext, useEffect, useState, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import Cookies from 'js-cookie'
import axios from 'axios'

import BagIcon from '../../../assets/svgs/Bag'
import ChatIcon from '../../../assets/svgs/ChatIcon'
import HomeIcon from '../../../assets/svgs/Home'
import Icon from '../../../assets/svgs/Icon'
import SearchIcon from '../../../assets/svgs/Search'
import ShareIcon from '../../../assets/svgs/Share'
import UserIcon from '../../../assets/svgs/User'
import InfoIcon from '../../../assets/svgs/Info'

import style from './DashHeader.module.css'
import UploadModal from '../files/UploadModal'
import StartYourJourrney from '../../../utils/StartJourney'

import GuestContext from '../../../contexts/GuestContext'
import { useAuthContext } from '../../../contexts/AuthContext'
import { useCV } from '../../../contexts/CVContext'
import useWindowWidth from '../../../hooks/useWindowWidth'

// Only these tabs are enabled
const ALLOWED_TABS = new Set(['Home', 'Profile'])

const DashHeader = ({
  selectedTab,
  handleSelectTab,
  openGuestInfo,
  setOpenGuestInfo,
  openReg,
  setOpenAuth,
  handleAuth,
  user,
}) => {
  const location = useLocation()
  const [openUpload, setOpenUpload] = useState(false)
  const { setGuestUser } = useContext(GuestContext)
  const { isAuthenticated, logout } = useAuthContext()
  const { isCVUploaded, setIsCVUploaded } = useCV()
  const [loading, setLoading] = useState(false)
  const innerWidth = useWindowWidth()

  const token = Cookies.get('token')
  const url = process.env.REACT_APP_API_URL || ''

  const toggleModal = () => setOpenGuestInfo(!openGuestInfo)

  // User dropdown (render only one of desktop/mobile menus at a time)
  const [openUserMenu, setOpenUserMenu] = useState(false)
  const userMenuRef = useRef(null)

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (!userMenuRef.current) return
      if (!userMenuRef.current.contains(e.target)) setOpenUserMenu(false)
    }
    document.addEventListener('click', onDocClick)
    return () => document.removeEventListener('click', onDocClick)
  }, [])

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setOpenUserMenu(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  // Close menu when route changes
  useEffect(() => setOpenUserMenu(false), [location.pathname])

  // Logout -> API + clear + external redirect
  const handleLogout = async (e) => {
    e?.preventDefault?.()
    try {
      await axios.get(`${url}/users/logout`, { withCredentials: true })
    } catch (error) {
      console.error('Error logging out:', error?.message || error)
    } finally {
      try {
        Cookies.remove('token')
        Cookies.remove('isCVUploaded')
      } catch {}
      logout?.()
      window.location.replace('https://chimpknows.com/')
    }
  }

  // My profile
  const goToProfile = () => {
    setOpenUserMenu(false)
    handleSelectTab('Profile')
  }

  // Fetch user's files (for CV flag)
  useEffect(() => {
    const fetchUserFiles = async () => {
      try {
        setLoading(true)
        const response = await axios.get(`${url}/ai/user/get-files`, {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        })
        const { files } = response.data
        const hasCV = files.length > 0
        setIsCVUploaded(hasCV)
        Cookies.set('isCVUploaded', hasCV)
      } catch (error) {
        console.error('Error fetching user files:', error)
      } finally {
        setLoading(false)
      }
    }

    if (isAuthenticated) fetchUserFiles()
  }, [isAuthenticated])

  return (
    <>
      <div
        className={`${style.dashHeader} ${
          selectedTab === 'Chat' ? style.hidden : ''
        } ${
          location.pathname === '/dashboard/connect/create-new-event' &&
          style.hiddenHeader
        }`}
      >
        <div className={style.icon}>
          <Icon color={'#432C5B'} />
        </div>

        {innerWidth > 768 && !isCVUploaded && !loading && (
          <StartYourJourrney
            handleAuth={handleAuth}
            openReg={openReg}
            setOpenAuth={setOpenAuth}
            user={user}
          />
        )}

        {/* DESKTOP MENU (Profile removed; in dropdown) */}
        <div className={style.menuItemsContainer}>
          {['Home', 'Spotlight', 'Connect', 'Roadmaps'].map((tab) => {
            const isEnabled = ALLOWED_TABS.has(tab)
            return (
              <div
                key={tab}
                className={`${style.menuItem} ${
                  isEnabled && selectedTab === tab ? style.highlightedTab : ''
                } ${!isEnabled ? style.disabled : ''}`}
                onClick={() => {
                  if (isEnabled) handleSelectTab(tab)
                }}
              >
                {tab}
              </div>
            )
          })}

          {!isAuthenticated && (
            <div onClick={toggleModal} className={style.menuItem}>
              <InfoIcon width={'30px'} height={'30px'} />
            </div>
          )}

          {/* Render desktop user menu only on desktop */}
          {isAuthenticated && innerWidth > 768 && (
            <div className={style.userMenuWrapper} ref={userMenuRef}>
              <div
                className={style.iconContainer}
                onClick={() => setOpenUserMenu((v) => !v)}
                role="button"
                aria-haspopup="menu"
                aria-expanded={openUserMenu}
              >
                <UserIcon width={'20'} height={'20'} color={'#ffffff'} />
              </div>

              {openUserMenu && (
                <div className={style.userMenu} role="menu">
                  <button
                    className={style.userMenuItem}
                    onClick={goToProfile}
                    role="menuitem"
                  >
                    My profile
                  </button>
                  <button
                    className={style.userMenuItem}
                    onClick={handleLogout}
                    role="menuitem"
                  >
                    Log out
                  </button>
                </div>
              )}
            </div>
          )}

          {!isAuthenticated && (
            <>
              <div
                className={style.dashButton}
                onClick={() => {
                  setOpenGuestInfo(false)
                  handleAuth(!openReg)
                  setOpenAuth('login')
                }}
              >
                Log in
              </div>
              <div
                className={style.dashButton}
                onClick={() => {
                  setOpenGuestInfo(false)
                  handleAuth(!openReg)
                  setOpenAuth('register')
                }}
              >
                Sign up
              </div>
            </>
          )}
        </div>

        {/* MOBILE TOP BAR */}
        <div className={style.mobileMenuContainer}>
          <div className={style.searchContainerMobile}>
            <SearchIcon />
            <input
              placeholder="Search for something..."
              className={style.searchInputMobile}
            />
          </div>

          {!isAuthenticated && (
            <div onClick={toggleModal}>
              <InfoIcon width={'30px'} height={'30px'} />
            </div>
          )}

          {/* Render mobile user menu only on mobile */}
          {isAuthenticated && innerWidth <= 768 && (
            <div className={style.userMenuWrapper} ref={userMenuRef}>
              <div
                className={style.iconContainer}
                onClick={() => setOpenUserMenu((v) => !v)}
                role="button"
                aria-haspopup="menu"
                aria-expanded={openUserMenu}
              >
                <UserIcon width={'20'} height={'20'} color={'#ffffff'} />
              </div>

              {openUserMenu && (
                <div className={style.userMenu} role="menu">
                  <button
                    className={style.userMenuItem}
                    onClick={goToProfile}
                    role="menuitem"
                  >
                    My profile
                  </button>
                  <button
                    className={style.userMenuItem}
                    onClick={handleLogout}
                    role="menuitem"
                  >
                    Log out
                  </button>
                </div>
              )}
            </div>
          )}

          {!isAuthenticated && (
            <>
              <div
                className={style.dashButton}
                onClick={() => {
                  setOpenGuestInfo(false)
                  handleAuth(!openReg)
                  setOpenAuth('login')
                }}
              >
                Log in
              </div>
              <div
                className={style.dashButton}
                onClick={() => {
                  setOpenGuestInfo(false)
                  handleAuth(!openReg)
                  setOpenAuth('register')
                }}
              >
                Sign up
              </div>
            </>
          )}
        </div>
      </div>

      {/* Journey banner (unchanged) */}
      {innerWidth < 768 &&
        !location.pathname.startsWith('/dashboard/chat') &&
        !location.pathname.startsWith('/dashboard/connect/create-new-event') &&
        !isCVUploaded &&
        !loading && (
          <StartYourJourrney
            handleAuth={handleAuth}
            openReg={openReg}
            setOpenAuth={setOpenAuth}
          />
        )}

      {/* Bottom mobile nav (Profile removed) */}
      <div className={style.mobileMenu}>
        {['Home', 'Chat', 'Spotlight', 'Connect'].map((tab) => {
          const IconComponent =
            tab === 'Home'
              ? HomeIcon
              : tab === 'Chat'
              ? ChatIcon
              : tab === 'Spotlight'
              ? BagIcon
              : ShareIcon

          const isEnabled = ALLOWED_TABS.has(tab)

          return (
            <div
              key={tab}
              className={`${style.mobileMenuItem} ${
                isEnabled && selectedTab === tab ? style.highlighted : ''
              } ${!isEnabled ? style.disabled : ''}`}
              onClick={() => {
                if (isEnabled) handleSelectTab(tab)
              }}
            >
              <IconComponent
                stroke={isEnabled && selectedTab === tab ? '2' : '1'}
                color={isEnabled ? null : '#0000001A'}
              />
            </div>
          )
        })}
      </div>

      {openUpload && (
        <div className="cover">
          <UploadModal openUpload={openUpload} setOpenUpload={setOpenUpload} />
        </div>
      )}
    </>
  )
}

export default DashHeader
